<script setup lang="ts">
import BookAnimation from "@/features/book-flip/components/BookAnimationWithWipe.vue";
import { useMate } from "@/stores/mate";
import { useMission } from "@/stores/mission";
import { Mate, MateDispatch } from "@/types/entity";
import { useElementBounding } from "@vueuse/core";
import { useRouteQuery } from "@vueuse/router";
import { Popup } from "vant";
import { computed, onMounted, ref } from "vue";

const missionStore = useMission();
const dispatchMission = ref<MateDispatch>();
const mateStore = useMate();
const containerRef = ref<HTMLElement | null>(null);
const { width: containerWidth, height: containerHeight } =
  useElementBounding(containerRef);
const maxHeight = computed(() => containerHeight.value ?? window.innerHeight);
const maxWidth = computed(() => containerWidth.value ?? window.innerWidth);

const mateId = useRouteQuery<number>("mateId", 0, {
  transform: (value) => Number(value),
});
const dispatchUuid = useRouteQuery<string>("dispatchUuid", "");
const resultCardRef = ref<HTMLElement | null>(null);
const mate = ref<Mate | undefined>();
const { height: resultCardHeight } = useElementBounding(resultCardRef);
const initialTop = computed(() => {
  return resultCardHeight.value
    ? resultCardHeight.value + 66 + 32 + window.innerHeight * 0.05
    : undefined;
});
const showDetail = ref(false);

const pages = computed(() => {
  return dispatchMission.value?.obtainedJournalzz?.map((journal) => ({
    header: `${journal.date} ${journal.weather}`,
    content: journal.content,
  }));
});

const loading = ref(false);
const initPage = async () => {
  dispatchMission.value = await missionStore.getMateDispatch(
    dispatchUuid.value,
    mateId.value ?? 0
  );

  if (!dispatchMission.value.obtainedJournalzz?.length) {
    loading.value = true;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return initPage();
  }

  const mateMap = new Map<number, Mate>();
  dispatchMission.value.dispatchMemberzz?.forEach((member) => {
    mateMap.set(member.mateId, member.mate!);
  });

  dispatchMission.value.obtainedJournalzz.forEach((journal) => {
    journal.mate = mateMap.get(journal.mateId);
  });

  mate.value = mateStore.getMyMateById(
    mateId.value || dispatchMission.value?.mateId
  );

  if (loading.value) loading.value = false;
};

onMounted(() => {
  initPage();
});
</script>
<template>
  <div
    v-if="dispatchMission"
    ref="containerRef"
    class="w-full h-full overflow-hidden"
  >
    <div class="absolute top-0 left-0 w-full h-full bg-container"></div>
    <div class="relative w-full h-full overflow-hidden z-[2]">
      <div
        v-if="loading"
        class="flex items-center justify-center h-full relative flex-col gap-4"
      >
        <div class="loading loading-ring loading-lg"></div>
        <div>OC 还在努力写手帐！请耐心等待呀！</div>
      </div>

      <template v-else>
        <MissionRewardCard
          ref="resultCardRef"
          :dispatch-mission="dispatchMission"
          v-if="dispatchMission?.dispatchMemberzz"
          class="mx-8 mt-8"
        />

        <BookAnimation
          v-if="pages"
          :max-height="maxHeight"
          :max-width="maxWidth"
          :pages="pages"
          :mate="mate!"
          :initial-top="initialTop"
          @show-detail="showDetail = true"
        >
        </BookAnimation>
      </template>
    </div>
    <Popup
      v-model:show="showDetail"
      position="bottom"
      class="grid grid-rows-[auto_1fr] h-[80%] overflow-hidden text-base-content"
    >
      <ThreadsNav show-back :go-back="() => (showDetail = false)">
        <template #center>手帐详情</template>
        <template #right> </template>
      </ThreadsNav>
      <div class="flex flex-col gap-2 p-2 overflow-y-auto text-base-content">
        <MissionLogItem
          v-for="journal in dispatchMission.obtainedJournalzz"
          :key="journal.id"
          :journal="journal"
          is-detail-page
        />
      </div>
    </Popup>
  </div>
  <div v-else class="flex items-center justify-center h-full">
    <div class="loading loading-spinner loading-lg"></div>
  </div>
</template>
<style scoped>
@keyframes backgroundScroll {
  from {
    background-position: 50% 10%;
  }
  to {
    background-position: 0 0;
  }
}

.bg-container {
  background-repeat: repeat;
  animation: backgroundScroll 10s linear infinite;
  opacity: 0.2;
  background-size: 100%;
  background-image: url("/img/game-pattern.png");
  /* background-blend-mode: soft-light; */
}
</style>
